import type ApiClient from '@core/api/client'
import ErrorPage from '@core/components/ErrorPage'
import LoadingScreen from '@core/components/LoadingScreen'
import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import {
  RedirectAdvocateRedemption,
  RedirectAdvocateRedemptionPast,
  RedirectR,
  RedirectR0,
  RedirectRefer,
} from './redirects'

const PhotoProductsApp = lazy(() => import('@features/photo-products'))
const DPFLandingPageWrapper = lazy(
  () => import('@features/photo-products/pages/landing/dpf/DPFLandingPageWrapper')
)
const CampaignLandingPage = lazy(
  () => import('@features/photo-products/pages/landing/CampaignLandingPage')
)
const LoopApp = lazy(() => import('@features/loop'))
const ReferralApp = lazy(() => import('@features/referral'))
const EgiftsApp = lazy(() => import('@features/egifts'))

export const createAppRouter = ({
  api,
  supportEmail,
  isPrintifyPreviewLogsActive,
  familyAlbumDownloadDeepLink,
  familyAlbumStoreDeepLink,
  familyAlbumAutoLoginUrl,
}: {
  /** The page's one ApiClient (main.tsx); its cart queue and checkout queue are per session. */
  api: ApiClient
  supportEmail: string
  isPrintifyPreviewLogsActive: boolean
  familyAlbumAutologinDeeplink: string
  familyAlbumDownloadDeepLink: string
  familyAlbumStoreDeepLink: string
  familyAlbumAutoLoginUrl: string
  explanationVideoUrl: string
}) => {
  return createBrowserRouter([
    // Paths for lazy loaded feature entry points
    {
      path: '/photo-products/dpf-landing-jp',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <DPFLandingPageWrapper variant="JP" />
        </Suspense>
      ),
    },
    {
      path: '/photo-products/dpf-landing-us',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <DPFLandingPageWrapper variant="US" />
        </Suspense>
      ),
    },
    {
      // Before the `/photo-products/*` wildcard, which would otherwise swallow
      // it -- the same reason the two dpf-landing paths are declared above.
      //
      // This literal is one of three copies of the Campaign URL shape, and
      // nothing enforces agreement between them: Hapi's `CAMPAIGN_URL_PREFIX`
      // builds the redirect, and admin's `campaignShopperUrl` shows the
      // operator what to paste into the FamilyAlbum dashboard. Change one and
      // change all three; admin already drifted once.
      // Hapi ADR 0005: one public URL per Campaign, and the Variant is never in
      // the URL. The server resolves the cohort; this route only names the
      // Campaign.
      path: '/photo-products/c/:slug',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <CampaignLandingPage />
        </Suspense>
      ),
    },
    {
      path: '/photo-products/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <PhotoProductsApp
            supportEmail={supportEmail}
            isPrintifyPreviewLogsActive={isPrintifyPreviewLogsActive}
          />
        </Suspense>
      ),
    },
    {
      path: '/loop/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <LoopApp familyAlbumAutoLoginUrl={familyAlbumAutoLoginUrl} />
        </Suspense>
      ),
    },
    {
      path: 'referral/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ReferralApp
            supportEmail={supportEmail}
            familyAlbumDownloadDeepLink={familyAlbumDownloadDeepLink}
            familyAlbumStoreDeepLink={familyAlbumStoreDeepLink}
            familyAlbumAutoLoginUrl={familyAlbumAutoLoginUrl}
            api={api}
          />
        </Suspense>
      ),
    },
    {
      path: '/egifts/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <EgiftsApp />
        </Suspense>
      ),
    },
    // Redirections for old routes
    {
      path: '/photo-gifts',
      element: <Navigate to="/photo-products" replace />,
    },
    {
      path: '/refer/:advocateReferralCode',
      element: <RedirectRefer />,
    },
    {
      path: '/advocate/:advocateReferralCode/redemptions/:redemptionId',
      element: <RedirectAdvocateRedemption />,
    },
    {
      path: '/advocate/:advocateReferralCode/redemptions/:redemptionId/past',
      element: <RedirectAdvocateRedemptionPast />,
    },
    {
      path: '/redeem',
      element: <Navigate to="/referral/redeem" replace />,
    },
    {
      path: '/r/:advocateReferralCode',
      element: <RedirectR />,
    },
    {
      path: '/r0/:advocateReferralCode',
      element: <RedirectR0 />,
    },
    {
      path: '/photo-products-tou',
      element: <Navigate to="/photo-products/tou" replace />,
    },
    {
      path: '/photo-products-supplemental-tou',
      element: <Navigate to="/photo-products/supplemental-tou" replace />,
    },
    {
      path: '/pod-modal-milestone-1-month',
      element: <Navigate to="/photo-products/landing/buy-now?headerType=milestone-1-month" replace />,
    },
    {
      path: '/pod-modal-milestone-3-month',
      element: <Navigate to="/photo-products/landing/buy-now?headerType=milestone-3-month" replace />,
    },
    {
      path: '/pod-modal-milestone-6-month',
      element: <Navigate to="/photo-products/landing/buy-now?headerType=milestone-6-month" replace />,
    },
    {
      path: '/pod-modal-milestone-9-month',
      element: <Navigate to="/photo-products/landing/buy-now?headerType=milestone-9-month" replace />,
    },
    {
      path: '/pod-lp-child-birthday',
      element: <Navigate to="/photo-products/landing/buy-now?headerType=child-birthday" replace />,
    },
    {
      path: '/pod-lp-adult-birthday',
      element: <Navigate to="/photo-products/landing/buy-now?headerType=adult-birthday" replace />,
    },
    {
      path: '*',
      element: <ErrorPage error={404} message="Page Not Found" />,
    },
  ])
}
